<script lang="ts">
  import { mdiCheckBold, mdiEyeOffOutline, mdiRestore } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import {
    applyCustomerImportReviewItemAction,
    fetchCustomerImportReviewItems,
    type CustomerImportReviewAction,
    type CustomerImportReviewItem,
  } from "../../../../../../../../api/kehrblatt/imports"
  import type { ImportReviewGroupView } from "../../../../_shared/_logic/import-review-projection"
  import {
    reviewActionLabel,
    reviewItemFacts,
    reviewItemPreviewText,
  } from "../../../../_shared/_logic/import-review-projection"

  const REVIEW_ITEM_BATCH_SIZE = 100
  const SEARCH_DEBOUNCE_MS = 250

  let {
    draftId,
    organizationId = null,
    group,
    onChanged = () => {},
  }: {
    draftId: string
    organizationId?: string | null
    group: ImportReviewGroupView
    onChanged?: () => void
  } = $props()

  let items = $state<CustomerImportReviewItem[]>([])
  let total = $state(0)
  let loading = $state(false)
  let error = $state<string | null>(null)
  let status = $state("open")
  let severity = $state("")
  let search = $state("")
  let actionPendingId = $state<string | null>(null)
  let loadToken = 0

  const statusOptions = [
    { name: "Offen", value: "open" },
    { name: "Erledigt", value: "resolved" },
    { name: "Ignoriert", value: "ignored" },
    { name: "Alle", value: "" },
  ]
  const severityOptions = [
    { name: "Alle", value: "" },
    { name: "Kritisch", value: "critical" },
    { name: "Hoch", value: "high" },
    { name: "Mittel", value: "medium" },
    { name: "Niedrig", value: "low" },
    { name: "Info", value: "info" },
  ]
  type QuerySnapshot = {
    status: string
    severity: string
    search: string
  }
  type ActionButtonType = "primaryButton" | "secondaryButton"

  const actionButtonType = (action: string): ActionButtonType =>
    action === "mark_resolved" ? "primaryButton" : "secondaryButton"
  const actionIcon = (action: string) => {
    if (action === "mark_resolved") return mdiCheckBold

    if (action === "reopen") return mdiRestore

    return mdiEyeOffOutline
  }

  const currentQuery = (): QuerySnapshot => ({
    status,
    severity,
    search: search.trim(),
  })

  const loadItems = async (query: QuerySnapshot = currentQuery()) => {
    const token = ++loadToken
    loading = true
    error = null
    try {
      const collected: CustomerImportReviewItem[] = []
      let nextOffset = 0
      let responseTotal = 0

      do {
        const response = await fetchCustomerImportReviewItems(
          draftId,
          {
            groupKey: group.group_key,
            status: query.status,
            severity: query.severity,
            search: query.search || null,
            limit: REVIEW_ITEM_BATCH_SIZE,
            offset: nextOffset,
          },
          organizationId,
        )

        if (token !== loadToken) return

        collected.push(...response.items)
        responseTotal = response.total
        nextOffset += response.limit || REVIEW_ITEM_BATCH_SIZE

        if (response.items.length === 0) break
      } while (collected.length < responseTotal)

      items = collected
      total = responseTotal
    } catch (err) {
      if (token !== loadToken) return

      console.error("customer_import_review_items_load_failed", err)
      error = "Die Details konnten nicht geladen werden."
    } finally {
      if (token === loadToken) loading = false
    }
  }

  const runAction = async (item: CustomerImportReviewItem, action: CustomerImportReviewAction) => {
    if (actionPendingId) return

    actionPendingId = item.id
    error = null
    try {
      await applyCustomerImportReviewItemAction(draftId, item.id, action, organizationId)
      await loadItems(currentQuery())
      onChanged()
    } catch (err) {
      console.error("customer_import_review_action_failed", err)
      error = "Die Aktion konnte nicht gespeichert werden."
    } finally {
      actionPendingId = null
    }
  }

  $effect(() => {
    const query = currentQuery()
    const timeout = window.setTimeout(() => {
      void loadItems(query)
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timeout)
  })
</script>

<section class="details">
  <div class="details-header">
    <p>{group.description}</p>
    <div class="filters form">
      <Input type="text" placeholder="Suche" bind:value={search} brightBackground={true} />
    </div>
  </div>

  {#if error}
    <p class="error">{error}</p>
  {/if}

  {#if loading}
    <p class="muted">Details werden geladen ...</p>
  {:else if items.length === 0}
    <p class="muted">Keine passenden Hinweise vorhanden.</p>
  {:else}
    <div class="item-list">
      {#each items as item (item.id)}
        <ActionBox label={item.title} darkMode={true}>
          <div class="review-item">
            <p>{reviewItemPreviewText(item)}</p>
            <dl>
              {#each reviewItemFacts(item) as fact (fact.label)}
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              {/each}
            </dl>
          </div>
          {#snippet buttonSnippet()}
            {#if item.available_actions.length > 0}
              <div class="actions">
                {#each item.available_actions as action (action)}
                  <CTA
                    type={actionButtonType(action)}
                    content={reviewActionLabel(action)}
                    icon={actionIcon(action)}
                    darkMode={false}
                    disabled={Boolean(actionPendingId)}
                    onClick={() => runAction(item, action as CustomerImportReviewAction)}
                  />
                {/each}
              </div>
            {/if}
          {/snippet}
        </ActionBox>
      {/each}
    </div>
  {/if}
  <p class="result-count">{total} Hinweise</p>
</section>

<style lang="less">
  .details {
    margin-top: 0;
    border: 0;
    border-radius: 0;
    padding: 0;
    background: transparent;
    color: var(--text-invers-100);
    display: grid;
    gap: 1rem;
  }

  .details-header {
    display: grid;
    gap: 1rem;
  }

  p {
    margin: 0;
    line-height: 1.45;
    color: var(--text-invers-100);
  }

  .filters {
    display: grid;
    grid-template-columns: minmax(12rem, 1fr) minmax(9rem, 0.65fr) minmax(9rem, 0.65fr);
    gap: 0.75rem;
    align-items: end;

    :global(label) {
      min-width: 0;
    }
  }

  .item-list {
    display: grid;
    gap: 0.9rem;
  }

  .review-item {
    display: grid;
    gap: 0.8rem;
    color: var(--text-invers-100);
  }

  .review-item dl {
    display: grid;
    grid-template-columns: minmax(7rem, max-content) minmax(0, 1fr);
    gap: 0.45rem 0.8rem;
    margin: 0;
  }

  .review-item dt {
    font-weight: 700;
    color: var(--text-invers-100);
  }

  .review-item dd {
    margin: 0;
    min-width: 0;
    color: var(--text-invers-100);
    overflow-wrap: anywhere;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .result-count {
    color: var(--text-invers-100);
  }

  .muted {
    color: var(--text-invers-100);
  }

  .error {
    margin-top: 1rem;
    color: #b91c1c;
    font-weight: 700;
  }

  @media (max-width: 860px) {
    .filters {
      grid-template-columns: 1fr;
    }
  }
</style>
